import clsx from 'clsx';
import { type ComponentProps, type FC, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { Typography } from '@/ui';

import { CardBlockquote } from './CardBlockquote';
import remarkGroupedCardsPlugin from './remarkGroupedCards';
import remarkSingleCardPlugin from './remarkSingleCard';
import { normalizeSlackToMarkdown } from './utils/slackish';

import './cards.css';

export interface SlackishMarkdownProps {
  className?: string;
  mentionMap?: Record<string, string>;
  resolveMention?: (id: string) => string;
  text: string;
}

type MarkdownRendererProps = ComponentProps<typeof ReactMarkdown>;
type MarkdownComponents = NonNullable<MarkdownRendererProps['components']>;

type MarkdownAnchorProps = ComponentProps<'a'>;
type MarkdownBlockquoteProps = ComponentProps<'blockquote'>;
type MarkdownDivProps = ComponentProps<'div'> & { node?: unknown };
type MarkdownHeadingProps = ComponentProps<'h3'>;
type MarkdownListItemProps = ComponentProps<'li'>;
type MarkdownListProps = ComponentProps<'ul'>;
type MarkdownParagraphProps = ComponentProps<'p'>;

const customSchema: typeof defaultSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    div: ['class', 'className'],
  },
  tagNames: [...(defaultSchema.tagNames || []), 'div'],
};

const MarkdownAnchor: FC<MarkdownAnchorProps> = ({ children, href }) => {
  const safeHref = typeof href === 'string' ? href : undefined;
  return (
    <a
      className="underline"
      href={safeHref}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </a>
  );
};

const MarkdownBlockquote: FC<MarkdownBlockquoteProps> = ({ children }) => (
  <CardBlockquote>{children}</CardBlockquote>
);

const MarkdownDiv: FC<MarkdownDivProps> = ({
  children,
  className: divClassName,
  node: _node,
  ...props
}) => {
  if (divClassName === 'grouped-cards') {
    return (
      <div
        aria-label="Related summary cards"
        className="grouped-cards"
        role="group"
        {...props}
      >
        {children}
      </div>
    );
  }

  if (typeof divClassName === 'string' && divClassName.includes('card-block')) {
    const cardTypeMatch = divClassName.match(/card-(win|warning|quote|note)/u);
    const cardType = cardTypeMatch ? cardTypeMatch[1] : undefined;

    return (
      <div
        aria-label={cardType ? `${cardType} card` : 'card'}
        className={divClassName}
        data-card-type={cardType}
        role="note"
        {...props}
      >
        <div className="card-content">{children}</div>
      </div>
    );
  }

  return (
    <div className={divClassName} {...props}>
      {children}
    </div>
  );
};

const MarkdownHeading: FC<MarkdownHeadingProps> = ({ children }) => (
  <Typography color="text.secondary" variant="overline">
    {children}
  </Typography>
);

const MarkdownListItem: FC<MarkdownListItemProps> = ({ children }) => (
  <li>
    <Typography color="text.secondary" component="div" variant="body2">
      {children}
    </Typography>
  </li>
);

const MarkdownParagraph: FC<MarkdownParagraphProps> = ({ children }) => (
  <Typography color="text.secondary" variant="body2">
    {children}
  </Typography>
);

const MarkdownList: FC<MarkdownListProps> = ({ children }) => (
  <ul className="m-0 list-disc pl-5">{children}</ul>
);

const markdownComponents: MarkdownComponents = {
  a: MarkdownAnchor as MarkdownComponents['a'],
  blockquote: MarkdownBlockquote as MarkdownComponents['blockquote'],
  div: MarkdownDiv as MarkdownComponents['div'],
  h3: MarkdownHeading as MarkdownComponents['h3'],
  li: MarkdownListItem as MarkdownComponents['li'],
  p: MarkdownParagraph as MarkdownComponents['p'],
  ul: MarkdownList as MarkdownComponents['ul'],
};

const SlackishMarkdown: FC<SlackishMarkdownProps> = ({
  className,
  mentionMap,
  resolveMention,
  text,
}) => {
  const markdown = useMemo(() => {
    const resolver =
      resolveMention ||
      (mentionMap ? (id: string) => `@${mentionMap[id] || id}` : undefined);

    return normalizeSlackToMarkdown(
      text || '',
      resolver ? { resolveMention: resolver } : undefined,
    );
  }, [mentionMap, resolveMention, text]);

  return (
    <div className={clsx('slackish-markdown', className)}>
      <ReactMarkdown
        components={markdownComponents}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, customSchema]]}
        remarkPlugins={[
          remarkGfm,
          remarkGroupedCardsPlugin,
          remarkSingleCardPlugin,
        ]}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
};

export default SlackishMarkdown;
